import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { authAPI, characterAPI, userAPI } from '../services/api';
import { setCredentials, logout as logoutAction } from '../store/slices/authSlice';
import { persistor } from '../store/store';
import type { LoginRequest, RegisterRequest } from '@shared/types/api.types';
import type { UpdateProfileRequest } from '../services/api/user.api';

/**
 * 로그인 Hook
 * @param redirectTo - 로그인 성공 후 리다이렉트할 경로 (기본: role 기반 자동 결정)
 * @param checkCharacter - 캐릭터 존재 여부 확인 (일반 사용자용, 기본: true)
 */
export function useLogin(redirectTo?: string, checkCharacter: boolean = true) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: LoginRequest) => {
      return authAPI.login(data);
    },
    onSuccess: async (response) => {
      // Redux에 인증 정보 저장
      dispatch(
        setCredentials({
          user: response.user,
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
        })
      );

      // Redux Persist가 localStorage에 저장 완료될 때까지 대기
      await persistor.flush();

      // 리다이렉트 경로가 명시된 경우 (트레이너/관리자 로그인)
      if (redirectTo) {
        navigate(redirectTo);
        return;
      }

      // 일반 사용자: 캐릭터 존재 여부 확인
      if (checkCharacter) {
        try {
          await characterAPI.getMyCharacter();
          navigate('/home');
        } catch (error: any) {
          if (error?.response?.status === 404) {
            navigate('/character/create');
          } else {
            navigate('/home');
          }
        }
      } else {
        navigate('/home');
      }
    },
    onError: (error) => {
      console.error('❌ Login failed:', error);
    },
  });
}

/**
 * 회원가입 Hook
 */
export function useRegister() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: RegisterRequest) => authAPI.register(data),
    onSuccess: async (response) => {
      // Redux에 인증 정보 저장
      dispatch(
        setCredentials({
          user: response.user,
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
        })
      );

      // Redux Persist가 localStorage에 저장 완료될 때까지 대기
      await persistor.flush();

      // 온보딩 목표 선택 페이지로 이동
      // 순서: 목표선택(1) → 프로필입력/캐릭터생성(2) → 레벨테스트(3)
      navigate('/onboarding/goal');
    },
  });
}

/**
 * 로그아웃 Hook
 */
export function useLogout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authAPI.logout(),
    onSuccess: () => {
      // Redux 상태 초기화
      dispatch(logoutAction());

      // React Query 캐시 초기화
      queryClient.clear();

      // 로그인 페이지로 이동
      navigate('/login');
    },
  });
}

/**
 * 현재 사용자 정보 조회 Hook
 */
export function useMe(enabled: boolean = true) {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => authAPI.getMe(),
    staleTime: 5 * 60 * 1000, // 5분
    retry: false,
    enabled, // false면 API 호출 안 함
  });
}

/**
 * 프로필 업데이트 Hook
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => userAPI.updateProfile(data),
    onSuccess: (response) => {
      // 캐시 업데이트
      queryClient.setQueryData(['me'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          nickname: response.nickname,
          gender: response.gender,
          height: response.height,
          weight: response.weight,
          birthdate: response.birthdate,
          profileImageUrl: response.profileImageUrl,
        };
      });

      // 프로필 페이지로 이동
      navigate('/profile');
    },
  });
}
